import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import {
  Refresh,
  Business,
  Group,
  TrendingUp,
  Warning,
  CheckCircle,
  Cancel,
  Archive,
  Settings,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { OrgStatus } from "../../types/auth";
// @ts-ignore - Known tRPC type issue
import { trpc } from "../../lib/api/trpc";

const Organizations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrgStatus | "">("");

  // Fetch organizations data
  // @ts-ignore - Known tRPC type issue
  const { data, isLoading, error, refetch } =
    trpc.admin.listOrganizations.useQuery({
      page,
      limit: rowsPerPage,
      search: searchTerm || undefined,
      status: statusFilter || undefined,
    });

  // @ts-ignore - Known tRPC type issue
  const { data: stats, isLoading: statsLoading } =
    trpc.admin.getGlobalStats.useQuery();

  // Check if user is SuperAdmin
  if (!user?.isSuperAdmin) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          Access Denied: SuperAdmin privileges required
        </Typography>
      </Box>
    );
  }

  const getStatusColor = (status: OrgStatus) => {
    switch (status) {
      case OrgStatus.TRIAL:
        return "warning";
      case OrgStatus.ACTIVE:
        return "success";
      case OrgStatus.SUSPENDED:
        return "error";
      case OrgStatus.ARCHIVED:
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: OrgStatus) => {
    switch (status) {
      case OrgStatus.TRIAL:
        return <Warning fontSize="small" />;
      case OrgStatus.ACTIVE:
        return <CheckCircle fontSize="small" />;
      case OrgStatus.SUSPENDED:
        return <Cancel fontSize="small" />;
      case OrgStatus.ARCHIVED:
        return <Archive fontSize="small" />;
      default:
        return null;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Organizations Management
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                  >
                    Total Organizations
                  </Typography>
                  <Typography variant="h4">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      stats?.totalOrganizations || 0
                    )}
                  </Typography>
                </Box>
                <Business color="primary" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                  >
                    Active Organizations
                  </Typography>
                  <Typography variant="h4">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      stats?.activeOrganizations || 0
                    )}
                  </Typography>
                </Box>
                <CheckCircle color="success" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                  >
                    Trial Organizations
                  </Typography>
                  <Typography variant="h4">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      stats?.trialOrganizations || 0
                    )}
                  </Typography>
                </Box>
                <Warning color="warning" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    color="textSecondary"
                    gutterBottom
                    variant="body2"
                  >
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {statsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      (stats?.totalUsers || 0).toLocaleString()
                    )}
                  </Typography>
                </Box>
                <Group color="secondary" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search organizations"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as OrgStatus | "")
                }
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={OrgStatus.TRIAL}>Trial</MenuItem>
                <MenuItem value={OrgStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={OrgStatus.SUSPENDED}>Suspended</MenuItem>
                <MenuItem value={OrgStatus.ARCHIVED}>Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Organizations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Organization</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Usage</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="error">
                    Error loading organizations
                  </Typography>
                </TableCell>
              </TableRow>
            ) : !data?.organizations || data.organizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    No organizations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.organizations.map((org: any) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {org.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {org.slug}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(org.status)}
                      label={org.status}
                      color={getStatusColor(org.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{org.plan.displayName}</TableCell>
                  <TableCell align="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="body2">
                        {org._count.members}/{org.plan.maxUsers}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ width: 100, mx: "auto" }}>
                      <LinearProgress
                        variant="determinate"
                        value={(org._count.members / org.plan.maxUsers) * 100}
                        color={
                          org._count.members / org.plan.maxUsers > 0.8
                            ? "warning"
                            : "primary"
                        }
                      />
                      <Typography variant="caption" color="textSecondary">
                        {Math.round(
                          (org._count.members / org.plan.maxUsers) * 100,
                        )}
                        %
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Strategy">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/admin/organizations/${org.id}/strategy`)
                        }
                      >
                        <Settings />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <TrendingUp />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default Organizations;
